import { App, ConfigProvider } from 'antd';

import enUs from 'antd/lib/locale/en_US';
import { useAppStore } from '../../store/store';
import { lightenHex } from '../../utils/utils';

/**
 * Header component to render the top navigation bar for the application.
 *
 * This method manages multiple features such as branding logo, global search field,
 * quick share upload status, and user interaction elements like logout and navigation buttons.
 * The visibility and behavior of different parts of the header adapt dynamically based on
 * user authentication status, current route, and application state.
 *
 * @return {JSX.Element} The rendered header component for the application's top navigation bar.
 */
export default function AntdConfigProvider({ children }: { children: React.ReactNode }) {
  const brandingData = useAppStore((s) => s.brandingData);
  const mainButtonsColorBackground = brandingData?.mainButtonsColorBackground || '#193057';
  const mainButtonsColorBackgroundHover = lightenHex(mainButtonsColorBackground, 12);

  const mainButtonsColorForeground = brandingData?.mainButtonsColorForeground || '#ffffff';

  return (
    <ConfigProvider
      locale={enUs}
      theme={{
        token: {
          colorPrimary: '#193057',
          colorInfo: '#193057',
          colorSuccess: '#00b048',
          colorError: '#f73b3b',
          borderRadius: 5,
          wireframe: true,
        },
        components: {
          Table: {
            lineHeight: 1,
            bodySortBg: 'rgb(255,255,255,0)',
            padding: 14,
            rowSelectedBg: 'rgba(25,48,87,0.1)',
            rowSelectedHoverBg: 'rgba(25,48,87,0.2)',
            motionDurationMid: '0s',
            motionDurationSlow: '0s',
            rowHoverBg: '#eeeffa',
          },
          Select: {
            optionSelectedColor: 'rgb(0,0,0)',
            optionSelectedBg: 'rgba(25,48,87,0.1)',
            activeOutlineColor: 'rgba(255,255,255,0)',
          },
          TreeSelect: {
            nodeSelectedBg: 'rgb(25,48,87,0.1)',
          },
          Input: {
            activeShadow: '0 0 0 0 rgba(255,255,255,0)',
            errorActiveShadow: '0 0 0 0 rgba(255,255,255,0)',
            warningActiveShadow: '0 0 0 0 rgba(255,255,255,0)',
          },
          Button: {
            colorPrimary: mainButtonsColorBackground,
            defaultShadow: '0 1px 0 rgba(0,0,0,0.01)',
            primaryShadow: '0 1px 0 rgba(5,145,255,0.1)',
            dangerShadow: '0 1px 0 rgba(255,38,5,0.06)',
            colorPrimaryHover: mainButtonsColorBackgroundHover,
            primaryColor: mainButtonsColorForeground,
          },
          List: {
            descriptionFontSize: 10,
            titleMarginBottom: 2,
          },
          Modal: {
            motionDurationMid: '0s',
            motionDurationSlow: '0s',
          },
          Dropdown: {
            motionDurationMid: '0.1s',
            motionEaseInOutCirc: 'ease-in-out',
            motionEaseInQuint: 'ease-in',
            motionEaseOutCirc: 'ease-out',
            motionEaseOutQuint: 'ease-out',
          },
        },
      }}
    >
      <App className="h-full">{children}</App>
    </ConfigProvider>
  );
}
